import { useState, useEffect, useRef } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

import { incrementVersion, getRequiredVersionName } from '../../../../../services/utils';
import { aspectRatiosMatch, contentTypeParams, isDynamicContent } from '../../../../../services/contents';
import { calculateImpressions } from '../../../../../services/display';
import { makeApiCall } from '../../../../../services/apiErrorHandlers';
import api from '../../../../../services/apiInstance';
import { redistributePendingShows } from '../../services/showsRedistribution';

const parseVersionNumber = v => {
  const match = v && /^V(\d+)$/i.exec(v);
  return match ? parseInt(match[1], 10) : NaN;
};

const getFolderIdentifier = selectTagContent => {
  if (!selectTagContent) return null;
  const candidate =
    selectTagContent.key != null && selectTagContent.key !== ''
      ? selectTagContent.key
      : (selectTagContent.id != null && selectTagContent.id !== ''
        ? selectTagContent.id
        : selectTagContent.label || selectTagContent.name);
  if (candidate == null || candidate === '') return null;
  return String(candidate);
};

const useContentActions = (props, libraryState) => {
  const { t } = useTranslation();
  const {
    cart,
    listOfContentsInCampaign,
    setListOfContentsInCampaign,
    customContents,
    groupedDisplays,
    contentSelected,
    remplaceContentFromCampaign,
    currentCampaign,
    initialBackup,
    updateRelationCampaignRequest,
    updateContentDisplayByContent,
    exitModal,
    updateCustomContents,
    reportSingleData,
    toleranceResolution,
    deleteContentFromCampaign,
    user,
    isSuperUser = false
  } = props;

  const { selectVersion, onChangeVersion, getLibraryContents, selectTagContent, pageContents } = libraryState;

  const [contentModal, setContentModal] = useState(null);
  const [visibleModalContent, setVisibleModalContent] = useState(false);
  const [visibleModalDeleteContent, setVisibleModalDeleteContent] = useState(false);
  const [contentToReplace, setContentToReplace] = useState(null);
  const [targetContentToReplace, setTargetContentToReplace] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalCreateVersionOpen, setIsModalCreateVersionOpen] = useState(false);
  const [visibleCanvaModal, setVisibleCanvaModal] = useState(false);
  const [alertModalVisible, setAlertModalVisible] = useState(false);
  const [alertModalType, setAlertModalType] = useState(null);
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictGroups, setConflictGroups] = useState({});
  const [pendingNonConflicting, setPendingNonConflicting] = useState([]);
  const [loadingAddAll, setLoadingAddAll] = useState(false);
  const [existingByResolution, setExistingByResolution] = useState({});
  const [usedFolderKeys, setUsedFolderKeys] = useState(new Set());
  const [dynamicAlertVisible, setDynamicAlertVisible] = useState(false);

  const prevRealCountRef = useRef(
    listOfContentsInCampaign.filter(c => !c.isPlaceholder).length
  );

  const markFolderAsUsed = () => {
    const id = getFolderIdentifier(selectTagContent);
    if (!id) return;
    const key = `${id}_${selectVersion || 'V1'}`;
    setUsedFolderKeys(prev => new Set([...prev, key]));
  };

  useEffect(() => {
    const currentRealCount = listOfContentsInCampaign.filter(c => !c.isPlaceholder).length;
    if (currentRealCount < prevRealCountRef.current && usedFolderKeys.size > 0) {
      setUsedFolderKeys(new Set());
    }
    prevRealCountRef.current = currentRealCount;
  }, [listOfContentsInCampaign]); // eslint-disable-line react-hooks/exhaustive-deps

  const showModalContent = content => {
    if (!content) return;
    setContentModal(content);
    setVisibleModalContent(true);
  };

  const cancelModalContent = () => {
    setContentModal(null);
    setVisibleModalContent(false);
  };

  const showModalDeleteContent = (value = false, content) => {
    setContentModal(content);
    setVisibleModalDeleteContent(value);
  };

  const updateContent = async () => {
    try {
      await makeApiCall(api.updateContent, contentModal.id, { archived: true });
      setVisibleModalDeleteContent(false);
      getLibraryContents(pageContents, selectTagContent);
    } catch (error) {
      console.error('Error updateContent:', error.message);
      message.error(error.message);
      setVisibleModalDeleteContent(false);
    }
  };

  const checkContentsAdded = id => listOfContentsInCampaign.some(content => content.id === id);

  const existContentOtherScreens = existingContent => {
    if (!groupedDisplays) return false;
    const existingDisplayIds = customContents
      .filter(cc => cc.content.id === existingContent.id)
      .map(cc => cc.displayId);
    const allDisplaysMatch = existingDisplayIds.every(displayId =>
      groupedDisplays.some(gd => gd.id === displayId)
    );
    if (allDisplaysMatch) return false;
    return true;
  };

  const wouldConflictInVersion = (width, height, version) => {
    const isApprovedCampaign = currentCampaign && currentCampaign.payment_status === 'approved';
    const tolerance = (isApprovedCampaign && toleranceResolution !== undefined)
      ? toleranceResolution
      : currentCampaign ? currentCampaign.tolerance_resolution : 0;
    const isPendingPayment = currentCampaign && currentCampaign.payment_status === 'pending';

    return listOfContentsInCampaign.some(item => {
      if (item.isPlaceholder) return false;
      if ((item.content_version_name || 'V1') !== version) return false;

      if (isPendingPayment) {
        return item.width === width && item.height === height;
      }

      if (item.width === width && item.height === height) return true;
      const [isMatch] = aspectRatiosMatch(item.width, item.height, width, height, tolerance);
      return isMatch;
    });
  };

  // A dynamic ad has no resolution of its own, so it fits every screen: it takes over
  // the whole version at once instead of walking the placeholder / conflict / best-match
  // path below, which exists to cover one resolution at a time.
  const addDynamicContent = contentCopy => {
    const version = contentCopy.content_version_name || 'V1';
    const updatedCustomContents = customContents.map(customContent =>
      customContent.active !== 0 &&
      (customContent.content.content_version_name || 'V1') === version
        ? { ...customContent, content: contentCopy }
        : customContent
    );
    // Screens with no output in this version get theirs from the resolution engine,
    // which matches the dynamic ad to every resolution. The contents it replaced leave
    // the campaign with their outputs.
    const remainingContents = listOfContentsInCampaign.filter(
      item =>
        (item.content_version_name || 'V1') !== version &&
        (!item.isPlaceholder ||
          updatedCustomContents.some(customContent => customContent.content.id === item.id))
    );

    setListOfContentsInCampaign([...remainingContents, contentCopy]);
    updateCustomContents(updatedCustomContents);

    setDynamicAlertVisible(true);
  };

  const addContents = content => {
    const requiredVersionName = getRequiredVersionName(listOfContentsInCampaign);
    const requiredVersionHasContent = listOfContentsInCampaign.some(
      item => !item.isPlaceholder && (item.content_version_name || 'V1') === requiredVersionName
    );
    const targetVersion = requiredVersionHasContent ? selectVersion : requiredVersionName;
    if (targetVersion !== selectVersion) onChangeVersion(targetVersion);

    const isRequiredVersion = targetVersion === requiredVersionName;
    const contentCopy = { ...content, content_version_name: targetVersion, isPlaceholder: false, isRequiredVersion };

    if (isDynamicContent(content.type)) return addDynamicContent(contentCopy);

    const isApprovedCampaign = currentCampaign && currentCampaign.payment_status === 'approved';
    const isUncommittedCampaign = !isApprovedCampaign;
    const tolerance = (isApprovedCampaign && toleranceResolution !== undefined)
      ? toleranceResolution
      : currentCampaign && currentCampaign.tolerance_resolution !== undefined
        ? currentCampaign.tolerance_resolution
        : 0;

    const cartInScope = groupedDisplays
      ? cart.filter(display => groupedDisplays.some(gd => gd.id === display.id))
      : cart;

    if (!isApprovedCampaign) {
      const screensNeedingExactUpgrade = cartInScope.filter(display => {
        const isNewContentExactForScreen =
          contentCopy.width === display.resolution_width && contentCopy.height === display.resolution_height;
        if (!isNewContentExactForScreen) return false;

        const currentRelation = customContents.find(customContent => customContent.displayId === display.id);
        if (!currentRelation || currentRelation.content.isPlaceholder) return false;

        const isCurrentContentExactForScreen =
          currentRelation.content.width === display.resolution_width &&
          currentRelation.content.height === display.resolution_height;
        if (isCurrentContentExactForScreen) return false;

        const [isCurrentContentPartialMatchForScreen] = aspectRatiosMatch(
          currentRelation.content.width,
          currentRelation.content.height,
          display.resolution_width,
          display.resolution_height,
          tolerance
        );
        return isCurrentContentPartialMatchForScreen;
      });

      if (screensNeedingExactUpgrade.length > 0) {
        const upgradedDisplayIds = screensNeedingExactUpgrade.map(display => display.id);
        const updatedCustomContents = customContents.map(customContent =>
          upgradedDisplayIds.includes(customContent.displayId)
            ? { ...customContent, content: contentCopy }
            : customContent
        );
        const contentListWithoutOrphanedPlaceholders = listOfContentsInCampaign.filter(
          item =>
            !item.isPlaceholder ||
            updatedCustomContents.some(customContent => customContent.content.id === item.id)
        );

        setListOfContentsInCampaign([...contentListWithoutOrphanedPlaceholders, contentCopy]);
        updateCustomContents(updatedCustomContents);
        return;
      }
    }

    const placeholderToReplace = listOfContentsInCampaign.find(
      item =>
        item.isPlaceholder &&
        item.width === contentCopy.width &&
        item.height === contentCopy.height &&
        item.content_version_name === contentCopy.content_version_name
    );

    if (placeholderToReplace) {
      const newList = listOfContentsInCampaign.map(item =>
        item.id === placeholderToReplace.id ? contentCopy : item
      );
      remplaceContentFromCampaign(placeholderToReplace.id, contentCopy);
      setListOfContentsInCampaign(newList);
      return;
    }

    if (contentSelected) {
      setContentToReplace(contentCopy);
      const isExistingContentOtherScreens = existContentOtherScreens(contentSelected);
      if (isExistingContentOtherScreens && !isUncommittedCampaign) return setIsModalCreateVersionOpen(true);
    }

    const isPendingPayment = currentCampaign && currentCampaign.payment_status === 'pending';

    const contentCandidates = groupedDisplays
      ? (() => {
          const groupedDisplayIds = groupedDisplays.map(display => display.id);
          const assignedIds = new Set(
            customContents
              .filter(customContent => groupedDisplayIds.includes(customContent.displayId))
              .map(customContent => customContent.content.id)
          );
          return listOfContentsInCampaign.filter(item => assignedIds.has(item.id));
        })()
      : listOfContentsInCampaign;

    const resolutionGroups = new Map();
    cartInScope.forEach(display => {
      const key = `${display.resolution_width}x${display.resolution_height}`;
      if (!resolutionGroups.has(key)) {
        resolutionGroups.set(key, {
          screenWidth: display.resolution_width,
          screenHeight: display.resolution_height,
          displayIds: []
        });
      }
      resolutionGroups.get(key).displayIds.push(display.id);
    });

    let bestGroup = null;
    let bestDifference = Infinity;
    resolutionGroups.forEach(group => {
      const isExactForGroup =
        group.screenWidth === contentCopy.width && group.screenHeight === contentCopy.height;
      if (isPendingPayment && !isExactForGroup) return;

      const [isMatch] = aspectRatiosMatch(
        contentCopy.width,
        contentCopy.height,
        group.screenWidth,
        group.screenHeight,
        tolerance
      );
      if (!isExactForGroup && !isMatch) return;

      const sizeDifference =
        Math.abs(contentCopy.width - group.screenWidth) / group.screenWidth +
        Math.abs(contentCopy.height - group.screenHeight) / group.screenHeight;
      if (sizeDifference < bestDifference) {
        bestDifference = sizeDifference;
        bestGroup = group;
      }
    });

    const bestGroupDisplayIds = new Set(bestGroup ? bestGroup.displayIds : []);

    const isExistingContent = contentCandidates.find(item => {
      if (item.isPlaceholder) return false;
      if (item.content_version_name !== contentCopy.content_version_name) return false;

      return customContents.some(
        customContent =>
          customContent.content &&
          customContent.content.id === item.id &&
          bestGroupDisplayIds.has(customContent.displayId)
      );
    });

    if (isExistingContent) {
      setContentToReplace(contentCopy);
      if (currentCampaign && currentCampaign.payment_status === 'approved') {
        setAlertModalType('change');
        setAlertModalVisible(true);
        return;
      }
      const isExistingContentOtherScreens = existContentOtherScreens(isExistingContent);
      if (isExistingContentOtherScreens && !isUncommittedCampaign) {
        setTargetContentToReplace(isExistingContent);
        return setIsModalCreateVersionOpen(true);
      }
      return setIsModalOpen(true);
    }

    setListOfContentsInCampaign([...listOfContentsInCampaign, contentCopy]);
  };

  const addContentsFromInfoCampaign = (content, relation) => {
    updateRelationCampaignRequest(content, relation, currentCampaign.id);
    const existingVersionNames = new Set(
      currentCampaign.contents_displays.map(cc => cc.content_version_name || 'V1')
    );
    const numbers = [...existingVersionNames].map(parseVersionNumber).filter(n => !isNaN(n));
    let nextNumber = (numbers.length ? Math.max(...numbers) : 1) + 1;
    while (existingVersionNames.has(`V${nextNumber}`)) nextNumber += 1;
    const nextVersion = `V${nextNumber}`;
    const convertedArray = [content].map(item => ({
      content_file: item.file,
      content_file_thumb: item.file_thumb,
      content_height: item.height,
      content_id: item.id,
      content_name: item.name,
      content_type: item.type,
      content_width: item.width,
      content_version_name: nextVersion
    }));
    updateContentDisplayByContent(convertedArray[0], relation);
    exitModal();
  };

  const replaceVersion = () => {
    let replacedContents = [];
    const isApprovedCampaign = currentCampaign && currentCampaign.payment_status === 'approved';
    const tolerance = (isApprovedCampaign && toleranceResolution !== undefined)
      ? toleranceResolution
      : currentCampaign && currentCampaign.tolerance_resolution !== undefined
        ? currentCampaign.tolerance_resolution
        : 0;
    const isPendingPayment = currentCampaign && currentCampaign.payment_status === 'pending';

    const isServingAsExactMatchElsewhere = content =>
      cart.some(display => {
        const relation = customContents.find(customContent => customContent.displayId === display.id);
        if (!relation || relation.content.id !== content.id) return false;

        const isContentExactForScreen =
          content.width === display.resolution_width && content.height === display.resolution_height;
        if (!isContentExactForScreen) return false;

        const isNewContentExactForScreen =
          contentToReplace.width === display.resolution_width &&
          contentToReplace.height === display.resolution_height;
        return !isNewContentExactForScreen;
      });

    const listContent = listOfContentsInCampaign.map(content => {
      if (contentSelected && content.id === contentSelected.id) {
        replacedContents.push(content);
        return contentToReplace;
      }

      if (isPendingPayment) {
        if (
          content.width === contentToReplace.width &&
          content.height === contentToReplace.height &&
          content.content_version_name === contentToReplace.content_version_name
        ) {
          replacedContents.push(content);
          return contentToReplace;
        }
        return content;
      }

      const [isMatch] = aspectRatiosMatch(
        content.width,
        content.height,
        contentToReplace.width,
        contentToReplace.height,
        tolerance
      );
      if (
        isMatch &&
        content.content_version_name === contentToReplace.content_version_name &&
        !isServingAsExactMatchElsewhere(content)
      ) {
        replacedContents.push(content);
        return contentToReplace;
      }
      return content;
    });

    if (replacedContents.length > 0) {
      const uniqueIds = [...new Set(replacedContents.map(c => c.id))];

      uniqueIds.forEach(id => {
        remplaceContentFromCampaign(id, contentToReplace);
      });
      setListOfContentsInCampaign(listContent);
    }

    setIsModalOpen(false);
  };

  const createVersion = () => {
    const newVersionContent = {
      ...contentToReplace,
      content_version_name: incrementVersion(listOfContentsInCampaign, contentToReplace),
      isRequiredVersion: false
    };

    if (groupedDisplays && targetContentToReplace) {
      const groupedDisplayIds = groupedDisplays.map(display => display.id);
      const updatedCustomContents = customContents.map(customContent =>
        groupedDisplayIds.includes(customContent.displayId) &&
        customContent.content.id === targetContentToReplace.id
          ? { ...customContent, content: newVersionContent }
          : customContent
      );
      setListOfContentsInCampaign([...listOfContentsInCampaign, newVersionContent]);
      updateCustomContents(updatedCustomContents);
      setIsModalCreateVersionOpen(false);
      setTargetContentToReplace(null);
      return;
    }

    setListOfContentsInCampaign([...listOfContentsInCampaign, newVersionContent]);
    setIsModalCreateVersionOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setContentToReplace(null);
    setTargetContentToReplace(null);
  };

  const handleModalCreateVersionCancel = () => {
    setIsModalCreateVersionOpen(false);
    setContentToReplace(null);
    setTargetContentToReplace(null);
  };

  const getNextVersion = () => {
    if (!listOfContentsInCampaign || listOfContentsInCampaign.length === 0) return 'V2';
    const versions = listOfContentsInCampaign
      .map(c => c.content_version_name)
      .filter(v => v && v.startsWith('V'))
      .map(v => parseInt(v.substring(1), 10))
      .filter(n => !isNaN(n));
    if (versions.length === 0) return 'V1';
    return `V${Math.max(...versions) + 1}`;
  };

  const isContentLinked = () => {
    if (!contentToReplace || !currentCampaign || !currentCampaign.contents_displays) return false;
    const isApprovedCampaign = currentCampaign && currentCampaign.payment_status === 'approved';
    const tolerance = (isApprovedCampaign && toleranceResolution !== undefined)
      ? toleranceResolution
      : currentCampaign && currentCampaign.tolerance_resolution !== undefined
        ? currentCampaign.tolerance_resolution
        : 0;
    const oldContent = listOfContentsInCampaign.find(item => {
      if (item.content_version_name !== contentToReplace.content_version_name) return false;
      const [isMatch] = aspectRatiosMatch(
        item.width,
        item.height,
        contentToReplace.width,
        contentToReplace.height,
        tolerance
      );
      return isMatch;
    });
    if (!oldContent) return false;
    if (groupedDisplays && groupedDisplays.length > 0) {
      const activeRelations = currentCampaign.contents_displays.filter(
        cd => cd.content_id === oldContent.id && cd.active === 1
      );
      const groupIds = groupedDisplays.map(d => d.id);
      return activeRelations.some(cd => !groupIds.includes(cd.display_id));
    }
    return false;
  };

  const getChangeAdDescription = () => {
    if (isContentLinked()) {
      return t(
        'This ad is linked to another screen. The change will only be made for this/these relationship(s) to avoid modifying the one currently in use. The current one will be deactivated and the promised outputs that have not yet been executed will be assigned to the new ad, which will be incorporated as a new version within the same campaign.'
      );
    }
    return t(
      'When changing the ad, the current one will become inactive and the promised outputs that have not yet been executed will be assigned to the new ad, which will be incorporated as a new version within the same campaign.'
    );
  };

  const computeChangeAdCore = (newContent, targetVersion, ctx) => {
    const {
      customContents: inputCustomContents,
      listOfContentsInCampaign: inputListOfContents,
      cart: ctxCart,
      groupedDisplays: ctxGroupedDisplays,
      currentCampaign: ctxCurrentCampaign,
      toleranceResolution: ctxTolerance,
      reportSingleData: ctxReportSingleData,
    } = ctx;

    const localTolerance = ctxTolerance !== undefined
      ? ctxTolerance
      : (ctxCurrentCampaign ? ctxCurrentCampaign.tolerance_resolution : 0);

    let newCustomContents = [...inputCustomContents];
    const newContentsForCampaignList = [];
    let changedCount = 0;
    let skippedInactiveCount = 0;
    let tempIdCounter = 0;
    const makeTempId = suffix => `temp_${Date.now()}_${++tempIdCounter}_${suffix}`;
    const touchedDisplayIds = new Set();

    const replacementContent = { ...newContent, content_version_name: targetVersion };

    const getNextVersionForDisplay = displayId => {
      const maxFromRelations = newCustomContents
        .filter(cc => cc.displayId === displayId)
        .reduce((max, cc) => {
          const n = parseVersionNumber(cc.content && cc.content.content_version_name);
          return isNaN(n) ? max : Math.max(max, n);
        }, 0);

      const maxFromList = (inputListOfContents || [])
        .filter(c => !c.isPlaceholder)
        .reduce((max, c) => {
          const n = parseVersionNumber(c.content_version_name);
          return isNaN(n) ? max : Math.max(max, n);
        }, 0);

      return `V${Math.max(maxFromRelations, maxFromList) + 1}`;
    };

    const displayPool = ctxGroupedDisplays && ctxGroupedDisplays.length > 0
      ? ctxGroupedDisplays
      : (ctxCart || []);
    const allDisplayIds = [...new Set(displayPool.map(d => d.id))];

    allDisplayIds.forEach(displayId => {
      const display = (ctxCart || []).find(d => d.id === displayId);
      if (!display) return;

      const screenWidth = display.resolution_width || display.display_resolution_width;
      const screenHeight = display.resolution_height || display.display_resolution_height;

      const [isMatch] = aspectRatiosMatch(
        replacementContent.width,
        replacementContent.height,
        screenWidth,
        screenHeight,
        localTolerance
      );
      if (!isMatch) return;

      const activeRelationIndex = newCustomContents.findIndex(
        item =>
          item.displayId === displayId &&
          item.active === 1 &&
          item.content.content_version_name === targetVersion
      );

      if (activeRelationIndex !== -1) {
        const oldRelation = newCustomContents[activeRelationIndex];
        const nextVersionForDisplay = getNextVersionForDisplay(displayId);
        const isTemporary =
          String(oldRelation.relationId).includes('_') || oldRelation.isFilledFromPlaceholder;

        if (isTemporary) {
          newCustomContents.push({
            ...(oldRelation || {}),
            relationId: makeTempId(`${displayId}_new`),
            parentRelationId: null,
            isReplacementAction: false,
            displayId,
            active: 1,
            promised_shows: 0,
            content: { ...replacementContent, content_version_name: nextVersionForDisplay },
            revision_status: 'pending',
            reject_reason: null,
            isPendingContent: false,
            subsidized_price: false,
            promised_shows_subsidized: 0
          });
          newContentsForCampaignList.push({
            ...replacementContent,
            content_version_name: nextVersionForDisplay,
            isRequiredVersion: false,
            _isFromReplacement: true
          });
          touchedDisplayIds.add(displayId);
          changedCount++;
        } else {
          const isApprovedPayment = ctxCurrentCampaign && ctxCurrentCampaign.payment_status === 'approved';
          if (isApprovedPayment && (oldRelation.promised_shows || 0) === 0) {
            return;
          }

          const remainingPaid = Math.max(
            0,
            calculateImpressions('to_consumed_not_subsidized', oldRelation, ctxReportSingleData, { id: displayId })
          );
          const remainingSubsidized = Math.max(
            0,
            calculateImpressions('to_consumed_subsidized', oldRelation, ctxReportSingleData, { id: displayId }) || 0
          );

          newCustomContents[activeRelationIndex] = {
            ...oldRelation,
            active: 0,
            promised_shows: (oldRelation.promised_shows || 0) - remainingPaid,
            promised_shows_subsidized: (oldRelation.promised_shows_subsidized || 0) - remainingSubsidized,
            revision_status: 'inactive'
          };

          newCustomContents.push({
            ...oldRelation,
            relationId: makeTempId(displayId),
            parentRelationId: oldRelation.relationId,
            isReplacementAction: true,
            displayId,
            active: 1,
            promised_shows: remainingPaid,
            content: { ...replacementContent, content_version_name: nextVersionForDisplay },
            revision_status: 'pending',
            reject_reason: null,
            isPendingContent: false,
            subsidized_price: false,
            promised_shows_subsidized: remainingSubsidized
          });

          newContentsForCampaignList.push({
            ...replacementContent,
            content_version_name: nextVersionForDisplay,
            isRequiredVersion: false,
            _isFromReplacement: true
          });
          changedCount++;
        }
      } else {
        const hasInactiveTargetVersion = newCustomContents.some(
          item =>
            item.displayId === displayId &&
            item.active !== 1 &&
            item.content.content_version_name === targetVersion
        );
        if (hasInactiveTargetVersion) {
          skippedInactiveCount++;
          return;
        }
        const displayIsActive = newCustomContents.some(
          item => item.displayId === displayId && item.active === 1
        );
        if (!displayIsActive) return;

        const templateRelation = newCustomContents.find(
          item => item.displayId === displayId && item.active === 1
        );
        const nextVersionForDisplay = getNextVersionForDisplay(displayId);

        newCustomContents.push({
          ...(templateRelation || {}),
          relationId: makeTempId(`${displayId}_new`),
          parentRelationId: null,
          isReplacementAction: false,
          displayId,
          active: 1,
          promised_shows: 0,
          content: { ...replacementContent, content_version_name: nextVersionForDisplay },
          revision_status: 'pending',
          reject_reason: null,
          isPendingContent: false,
          subsidized_price: false,
          promised_shows_subsidized: 0
        });

        newContentsForCampaignList.push({
          ...replacementContent,
          content_version_name: nextVersionForDisplay,
          isRequiredVersion: false,
          _isFromReplacement: true
        });
        touchedDisplayIds.add(displayId);
        changedCount++;
      }
    });

    touchedDisplayIds.forEach(displayId => {
      const activeRelations = newCustomContents.filter(
        cc => cc.displayId === displayId && cc.active === 1
      );
      if (activeRelations.length <= 1) return;

      const { updates } = redistributePendingShows({
        relations: activeRelations,
        reportSingleData: ctxReportSingleData,
        displayId,
      });

      activeRelations.forEach((rel, i) => {
        const idx = newCustomContents.indexOf(rel);
        if (idx !== -1) newCustomContents[idx] = { ...rel, ...updates[i] };
      });
    });

    return { newCustomContents, newContentsForCampaignList, changedCount, skippedInactiveCount };
  };

  const handleChangeAd = () => {
    const ctx = {
      customContents,
      listOfContentsInCampaign,
      cart,
      groupedDisplays,
      currentCampaign,
      toleranceResolution,
      reportSingleData,
    };

    const targetVersion = contentToReplace.content_version_name;
    const result = computeChangeAdCore(contentToReplace, targetVersion, ctx);

    if (result.changedCount === 0) {
      if (result.skippedInactiveCount > 0) {
        message.warning(t('The selected version is inactive. Please select a valid version.'));
      } else {
        message.warning(
          t('The new ad does not match the resolution of any of the screens. No changes were made.')
        );
      }
      setAlertModalVisible(false);
      setContentToReplace(null);
      return;
    }

    if (result.skippedInactiveCount > 0) {
      message.info(
        t(
          'The new version has only been incorporated into those screens that had the previous version active.'
        )
      );
    } else {
      message.success(t('Ad successfully updated.'));
    }

    updateCustomContents(result.newCustomContents);

    const uniqueNewContents = result.newContentsForCampaignList.filter(
      (v, i, a) =>
        a.findIndex(x => x.id === v.id && x.content_version_name === v.content_version_name) ===
          i &&
        !listOfContentsInCampaign.some(
          c => c.id === v.id && c.content_version_name === v.content_version_name
        )
    );

    if (uniqueNewContents.length > 0) {
      setListOfContentsInCampaign([...listOfContentsInCampaign, ...uniqueNewContents]);
    }

    setAlertModalVisible(false);
    setContentToReplace(null);
  };

  const findNextAvailableVersion = (width, height, accumulatedList) => {
    const existingNames = new Set(
      accumulatedList
        .filter(c => c.width === width && c.height === height && !c.isPlaceholder)
        .map(c => c.content_version_name || 'V1')
    );

    if (!existingNames.has(selectVersion)) return selectVersion;

    const existingVersionNumbers = [...existingNames].map(parseVersionNumber).filter(n => !isNaN(n));
    let next = (existingVersionNumbers.length ? Math.max(...existingVersionNumbers) : 1) + 1;
    while (existingNames.has(`V${next}`)) next += 1;
    return `V${next}`;
  };

  const buildVersionedContents = (contents, startingList) => {
    const accumulatedList = [...startingList];
    const result = [];

    contents.forEach(content => {
      const assignedVersion = findNextAvailableVersion(
        content.width,
        content.height,
        accumulatedList
      );
      const withVersion = { ...content, content_version_name: assignedVersion, isPlaceholder: false };
      result.push(withVersion);
      accumulatedList.push(withVersion);
    });

    return result;
  };

  const findExistingMatchForBulk = (newContent, currentList) => {
    const paymentStatus = currentCampaign && currentCampaign.payment_status;
    const isPendingPayment = paymentStatus === 'pending';
    const isApprovedPayment = paymentStatus === 'approved';
    const tolerance = (isApprovedPayment && toleranceResolution !== undefined)
      ? toleranceResolution
      : currentCampaign && currentCampaign.tolerance_resolution !== undefined
        ? currentCampaign.tolerance_resolution
        : 0;

    const newVersion = newContent.content_version_name || 'V1';

    return currentList.find(item => {
      if (item.isPlaceholder) return false;
      if ((item.content_version_name || 'V1') !== newVersion) return false;

      if (isPendingPayment) {
        return item.width === newContent.width && item.height === newContent.height;
      }
      if (item.width === newContent.width && item.height === newContent.height) return true;
      const [isMatch] = aspectRatiosMatch(
        item.width,
        item.height,
        newContent.width,
        newContent.height,
        tolerance
      );
      return isMatch;
    });
  };

  const detectExistingConflicts = folderContents => {
    const folderByResolution = folderContents.reduce((groups, content) => {
      const key = `${content.width}x${content.height}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(content);
      return groups;
    }, {});

    const existingMap = {};
    Object.keys(folderByResolution).forEach(key => {
      const taggedRepresentative = {
        ...folderByResolution[key][0],
        content_version_name: selectVersion,
      };
      const match = findExistingMatchForBulk(taggedRepresentative, listOfContentsInCampaign);
      if (match) existingMap[key] = match;
    });

    const conflictGroupsOut = {};
    const cleanContents = [];

    Object.entries(folderByResolution).forEach(([key, contents]) => {
      const hasFolderInternalConflict = contents.length > 1;
      const hasExistingMatch = !!existingMap[key];
      if (hasFolderInternalConflict || hasExistingMatch) {
        conflictGroupsOut[key] = contents;
      } else {
        cleanContents.push(contents[0]);
      }
    });

    return { conflictGroups: conflictGroupsOut, existingMap, cleanContents };
  };

  const appendWithVersion = (content, accumulatedList) => {
    const version = findNextAvailableVersion(content.width, content.height, accumulatedList);
    return { ...content, content_version_name: version, isPlaceholder: false };
  };

  const removeRedundantPlaceholders = list =>
    list.filter(item => {
      if (!item.isPlaceholder) return true;
      const itemVersion = item.content_version_name || 'V1';
      return !list.some(
        other =>
          !other.isPlaceholder &&
          other.width === item.width &&
          other.height === item.height &&
          (other.content_version_name || 'V1') === itemVersion
      );
    });

  const computeChangeAdResult = (newContent, existingMatch, ctx) => {
    const localTolerance = ctx.toleranceResolution !== undefined
      ? ctx.toleranceResolution
      : (ctx.currentCampaign ? ctx.currentCampaign.tolerance_resolution : 0);

    let newCustomContents = [...ctx.customContents];
    const newContentsForCampaignList = [];
    let changedCount = 0;
    let skippedInactiveCount = 0;
    let tempIdCounter = 0;
    const makeTempId = suffix => `temp_${Date.now()}_${++tempIdCounter}_${suffix}`;
    const touchedDisplayIds = new Set();

    const targetVersion = existingMatch.content_version_name;
    const replacementContent = { ...newContent, content_version_name: targetVersion };

    const getNextVersionForDisplay = displayId => {
      const versionsOnDisplay = new Set();
      newCustomContents.forEach(cc => {
        if (cc.displayId !== displayId) return;
        const v = cc.content && cc.content.content_version_name;
        if (v) versionsOnDisplay.add(v);
      });

      if (!versionsOnDisplay.has(selectVersion)) return selectVersion;

      const numbers = [...versionsOnDisplay].map(parseVersionNumber).filter(n => !isNaN(n));
      let next = (numbers.length ? Math.max(...numbers) : 1) + 1;
      while (versionsOnDisplay.has(`V${next}`)) next += 1;
      return `V${next}`;
    };

    const displayPool = ctx.groupedDisplays && ctx.groupedDisplays.length > 0
      ? ctx.groupedDisplays
      : (ctx.cart || []);
    const allDisplayIds = [...new Set(displayPool.map(d => d.id))];

    allDisplayIds.forEach(displayId => {
      const display = (ctx.cart || []).find(d => d.id === displayId);
      if (!display) return;

      const screenWidth = display.resolution_width || display.display_resolution_width;
      const screenHeight = display.resolution_height || display.display_resolution_height;

      const [isMatch] = aspectRatiosMatch(
        replacementContent.width,
        replacementContent.height,
        screenWidth,
        screenHeight,
        localTolerance
      );
      if (!isMatch) return;

      const activeRelationIndex = newCustomContents.findIndex(
        item =>
          item.displayId === displayId &&
          item.active === 1 &&
          item.content.content_version_name === targetVersion
      );

      if (activeRelationIndex !== -1) {
        const oldRelation = newCustomContents[activeRelationIndex];
        const nextVersionForDisplay = getNextVersionForDisplay(displayId);
        const isTemporary =
          String(oldRelation.relationId).includes('_') || oldRelation.isFilledFromPlaceholder;

        if (isTemporary) {
          newCustomContents.push({
            ...(oldRelation || {}),
            relationId: makeTempId(`${displayId}_new`),
            parentRelationId: null,
            isReplacementAction: false,
            displayId,
            active: 1,
            promised_shows: 0,
            content: { ...replacementContent, content_version_name: nextVersionForDisplay },
            revision_status: 'pending',
            reject_reason: null,
            isPendingContent: false,
            subsidized_price: false,
            promised_shows_subsidized: 0
          });
          newContentsForCampaignList.push({
            ...replacementContent,
            content_version_name: nextVersionForDisplay,
            isRequiredVersion: false,
            _isFromReplacement: true
          });
          touchedDisplayIds.add(displayId);
          changedCount++;
        } else {
          const isApprovedPayment =
            ctx.currentCampaign && ctx.currentCampaign.payment_status === 'approved';
          if (isApprovedPayment && (oldRelation.promised_shows || 0) === 0) {
            return;
          }

          let consumedShows = 0;
          if (ctx.reportSingleData && ctx.reportSingleData.report) {
            const reportItem = ctx.reportSingleData.report.find(
              r => r.display === displayId && r.content === oldRelation.content.id
            );
            if (reportItem) consumedShows = reportItem.shows || 0;
          }

          newCustomContents[activeRelationIndex] = {
            ...oldRelation,
            active: 0,
            promised_shows: consumedShows,
            revision_status: 'inactive'
          };

          const remainingShows = Math.max(0, (oldRelation.promised_shows || 0) - consumedShows);

          newCustomContents.push({
            ...oldRelation,
            relationId: makeTempId(displayId),
            parentRelationId: oldRelation.relationId,
            isReplacementAction: true,
            displayId,
            active: 1,
            promised_shows: remainingShows,
            content: { ...replacementContent, content_version_name: nextVersionForDisplay },
            revision_status: 'pending',
            reject_reason: null,
            isPendingContent: false,
            subsidized_price: false,
            promised_shows_subsidized: 0
          });

          newContentsForCampaignList.push({
            ...replacementContent,
            content_version_name: nextVersionForDisplay,
            isRequiredVersion: false,
            _isFromReplacement: true
          });
          changedCount++;
        }
      } else {
        const hasInactiveTargetVersion = newCustomContents.some(
          item =>
            item.displayId === displayId &&
            item.active !== 1 &&
            item.content.content_version_name === targetVersion
        );
        if (hasInactiveTargetVersion) {
          skippedInactiveCount++;
          return;
        }
        const displayIsActive = newCustomContents.some(
          item => item.displayId === displayId && item.active === 1
        );
        if (!displayIsActive) return;

        const templateRelation = newCustomContents.find(
          item => item.displayId === displayId && item.active === 1
        );
        const nextVersionForDisplay = getNextVersionForDisplay(displayId);

        newCustomContents.push({
          ...(templateRelation || {}),
          relationId: makeTempId(`${displayId}_new`),
          parentRelationId: null,
          isReplacementAction: false,
          displayId,
          active: 1,
          promised_shows: 0,
          content: { ...replacementContent, content_version_name: nextVersionForDisplay },
          revision_status: 'pending',
          reject_reason: null,
          isPendingContent: false,
          subsidized_price: false,
          promised_shows_subsidized: 0
        });

        newContentsForCampaignList.push({
          ...replacementContent,
          content_version_name: nextVersionForDisplay,
          isRequiredVersion: false,
          _isFromReplacement: true
        });
        touchedDisplayIds.add(displayId);
        changedCount++;
      }
    });

    touchedDisplayIds.forEach(displayId => {
      const activeRelations = newCustomContents.filter(
        cc => cc.displayId === displayId && cc.active === 1
      );
      if (activeRelations.length <= 1) return;

      const { updates } = redistributePendingShows({
        relations: activeRelations,
        reportSingleData: ctx.reportSingleData,
        displayId,
      });

      activeRelations.forEach((rel, i) => {
        const idx = newCustomContents.indexOf(rel);
        if (idx !== -1) newCustomContents[idx] = { ...rel, ...updates[i] };
      });
    });

    return { newCustomContents, newContentsForCampaignList, changedCount, skippedInactiveCount };
  };

  const computeAddAdditionalVersionResult = (newContent, ctx) => {
    const localTolerance = ctx.toleranceResolution !== undefined
      ? ctx.toleranceResolution
      : (ctx.currentCampaign ? ctx.currentCampaign.tolerance_resolution : 0);

    let newCustomContents = [...ctx.customContents];
    const newContentsForCampaignList = [];
    let changedCount = 0;
    let tempIdCounter = 0;
    const makeTempId = suffix => `temp_${Date.now()}_${++tempIdCounter}_${suffix}`;
    const touchedDisplayIds = new Set();

    const getNextVersionForDisplay = displayId => {
      const versionsOnDisplay = new Set();
      newCustomContents.forEach(cc => {
        if (cc.displayId !== displayId) return;
        const v = cc.content && cc.content.content_version_name;
        if (v) versionsOnDisplay.add(v);
      });

      if (!versionsOnDisplay.has(selectVersion)) return selectVersion;

      const numbers = [...versionsOnDisplay].map(parseVersionNumber).filter(n => !isNaN(n));
      let next = (numbers.length ? Math.max(...numbers) : 1) + 1;
      while (versionsOnDisplay.has(`V${next}`)) next += 1;
      return `V${next}`;
    };

    const displayPool = ctx.groupedDisplays && ctx.groupedDisplays.length > 0
      ? ctx.groupedDisplays
      : (ctx.cart || []);
    const allDisplayIds = [...new Set(displayPool.map(d => d.id))];

    allDisplayIds.forEach(displayId => {
      const display = (ctx.cart || []).find(d => d.id === displayId);
      if (!display) return;

      const screenWidth = display.resolution_width || display.display_resolution_width;
      const screenHeight = display.resolution_height || display.display_resolution_height;

      const [isMatch] = aspectRatiosMatch(
        newContent.width,
        newContent.height,
        screenWidth,
        screenHeight,
        localTolerance
      );
      if (!isMatch) return;

      const templateRelation = newCustomContents.find(
        item => item.displayId === displayId && item.active === 1
      );
      if (!templateRelation) return;

      const nextVersionForDisplay = getNextVersionForDisplay(displayId);

      newCustomContents.push({
        ...templateRelation,
        relationId: makeTempId(`${displayId}_additional`),
        parentRelationId: null,
        isReplacementAction: false,
        displayId,
        active: 1,
        promised_shows: 0,
        content: { ...newContent, content_version_name: nextVersionForDisplay },
        revision_status: 'pending',
        reject_reason: null,
        isPendingContent: false,
        subsidized_price: false,
        promised_shows_subsidized: 0
      });

      newContentsForCampaignList.push({
        ...newContent,
        content_version_name: nextVersionForDisplay,
        isRequiredVersion: false,
        _isFromReplacement: true
      });
      touchedDisplayIds.add(displayId);
      changedCount++;
    });

    touchedDisplayIds.forEach(displayId => {
      const activeRelations = newCustomContents.filter(
        cc => cc.displayId === displayId && cc.active === 1
      );
      if (activeRelations.length <= 1) return;

      const { updates } = redistributePendingShows({
        relations: activeRelations,
        reportSingleData: ctx.reportSingleData,
        displayId,
      });

      activeRelations.forEach((rel, i) => {
        const idx = newCustomContents.indexOf(rel);
        if (idx !== -1) newCustomContents[idx] = { ...rel, ...updates[i] };
      });
    });

    return { newCustomContents, newContentsForCampaignList, changedCount, skippedInactiveCount: 0 };
  };

  const computeReplaceVersionResult = (newContent, existingMatch, currentList) => {
    const replacementContent = {
      ...newContent,
      content_version_name: existingMatch.content_version_name,
      isPlaceholder: false,
    };
    const newList = currentList.map(content =>
      content.id === existingMatch.id &&
      (content.content_version_name || 'V1') === (existingMatch.content_version_name || 'V1')
        ? replacementContent
        : content
    );
    return {
      newList,
      redundantContentId: existingMatch.id,
      replacementContent,
    };
  };

  const addAllContentsFromFolder = folderContents => {
    const { conflictGroups: detectedConflicts, existingMap, cleanContents } =
      detectExistingConflicts(folderContents);

    if (Object.keys(detectedConflicts).length === 0) {
      if (cleanContents.length > 0) {
        const contentsToAdd = buildVersionedContents(cleanContents, listOfContentsInCampaign);
        const merged = [...listOfContentsInCampaign, ...contentsToAdd];
        const cleaned = removeRedundantPlaceholders(merged);
        const newList = listOfContentsInCampaign.map(item => {
          if (!item.isPlaceholder) return item;
          const itemVersion = item.content_version_name || 'V1';
          const replacement = cleaned.find(
            real =>
              !real.isPlaceholder &&
              real.width === item.width &&
              real.height === item.height &&
              (real.content_version_name || 'V1') === itemVersion
          );
          if (replacement) remplaceContentFromCampaign(item.id, replacement);
          return replacement || item;
        });
        const usedKeys = new Set(newList.filter(c => !c.isPlaceholder).map(c => `${c.id}_${c.content_version_name || 'V1'}`));
        const extra = cleaned.filter(c => !c.isPlaceholder && !usedKeys.has(`${c.id}_${c.content_version_name || 'V1'}`));
        setListOfContentsInCampaign([...newList, ...extra]);
        message.success(`${contentsToAdd.length} ${t('ads added to campaign')}.`);
      }
      markFolderAsUsed();
      return;
    }

    setConflictGroups(detectedConflicts);
    setExistingByResolution(existingMap);
    setPendingNonConflicting(cleanContents);
    setConflictModalOpen(true);
  };

  const confirmAddAll = selectedByResolution => {
    const paymentStatus = currentCampaign && currentCampaign.payment_status;
    const isApprovedPayment = paymentStatus === 'approved';

    let newContentList = [...listOfContentsInCampaign];
    let newCustomContents = [...customContents];
    const replacementsToDispatch = [];

    let appendedCount = 0;
    let versionedCount = 0;
    let replacedCount = 0;
    let changedAdRelationsCount = 0;
    let skippedInactiveCount = 0;
    let usedApprovedChange = false;

    pendingNonConflicting.forEach(content => {
      const versioned = appendWithVersion(content, newContentList);
      newContentList = [...newContentList, versioned];
      appendedCount++;
    });

    Object.entries(selectedByResolution).forEach(([resolutionKey, selectedContents]) => {
      const existingMatch = existingByResolution[resolutionKey];

      selectedContents.forEach((content, index) => {
        if (existingMatch && isApprovedPayment) {
          const ctx = {
            customContents: newCustomContents,
            listOfContentsInCampaign: newContentList,
            cart,
            groupedDisplays,
            currentCampaign,
            toleranceResolution,
            reportSingleData,
          };
          const result = index === 0
            ? computeChangeAdResult(content, existingMatch, ctx)
            : computeAddAdditionalVersionResult(content, ctx);

          if (result.changedCount === 0 && result.skippedInactiveCount === 0) {
            return;
          }

          newCustomContents = result.newCustomContents;
          result.newContentsForCampaignList.forEach(c => {
            const alreadyPresent = newContentList.some(
              x => x.id === c.id && x.content_version_name === c.content_version_name
            );
            if (!alreadyPresent) newContentList = [...newContentList, c];
          });
          changedAdRelationsCount += result.changedCount;
          skippedInactiveCount += result.skippedInactiveCount;
          if (result.changedCount > 0) usedApprovedChange = true;
          return;
        }

        if (existingMatch && index === 0) {
          if (existContentOtherScreens(existingMatch)) {
            const versioned = appendWithVersion(content, newContentList);
            newContentList = [...newContentList, versioned];
            versionedCount++;
            return;
          }
          const result = computeReplaceVersionResult(content, existingMatch, newContentList);
          newContentList = result.newList;
          replacementsToDispatch.push({
            contentId: result.redundantContentId,
            contentToReplace: result.replacementContent,
          });
          replacedCount++;
          return;
        }

        const versioned = appendWithVersion(content, newContentList);
        newContentList = [...newContentList, versioned];
        if (existingMatch) versionedCount++;
        else appendedCount++;
      });
    });

    if (newContentList !== listOfContentsInCampaign) {
      const cleaned = removeRedundantPlaceholders(newContentList);
      const resolvedList = listOfContentsInCampaign.map(item => {
        if (!item.isPlaceholder) return item;
        const itemVersion = item.content_version_name || 'V1';
        const replacement = cleaned.find(
          real =>
            !real.isPlaceholder &&
            real.width === item.width &&
            real.height === item.height &&
            (real.content_version_name || 'V1') === itemVersion
        );
        if (replacement) remplaceContentFromCampaign(item.id, replacement);
        return replacement || item;
      });
      const usedKeys = new Set(resolvedList.filter(c => !c.isPlaceholder).map(c => `${c.id}_${c.content_version_name || 'V1'}`));
      const extra = cleaned.filter(c => !c.isPlaceholder && !usedKeys.has(`${c.id}_${c.content_version_name || 'V1'}`));
      setListOfContentsInCampaign([...resolvedList, ...extra]);
    }

    if (usedApprovedChange) {
      updateCustomContents(newCustomContents);
    }

    replacementsToDispatch.forEach(({ contentId, contentToReplace }) => {
      remplaceContentFromCampaign(contentId, contentToReplace);
    });

    const summaryParts = [];
    if (appendedCount > 0) summaryParts.push(`${appendedCount} ${t('ads added to campaign')}`);
    if (versionedCount > 0) summaryParts.push(`${versionedCount} ${t('new versions created')}`);
    if (replacedCount > 0) summaryParts.push(`${replacedCount} ${t('ads replaced')}`);
    if (changedAdRelationsCount > 0)
      summaryParts.push(`${changedAdRelationsCount} ${t('relations updated')}`);
    if (summaryParts.length > 0) message.success(`${summaryParts.join(', ')}.`);
    if (skippedInactiveCount > 0) {
      message.info(
        `${skippedInactiveCount} ${t('contents omitted because target version is inactive')}`
      );
    }

    markFolderAsUsed();

    setConflictModalOpen(false);
    setPendingNonConflicting([]);
    setConflictGroups({});
    setExistingByResolution({});
  };

  const cancelAddAll = () => {
    setConflictModalOpen(false);
    setPendingNonConflicting([]);
    setConflictGroups({});
    setExistingByResolution({});
  };

  const triggerAddAllFromFolder = async () => {
    if (!selectTagContent || !selectTagContent.key) return;

    setLoadingAddAll(true);
    try {
      const fetchParams = {
        start: 0,
        length: 9999,
        page: 1,
        draw: 1,
        datatable: true,
        origin: 'dsp',
        // Mirrors the library listing: dynamic ads are only in reach for super users.
        type: contentTypeParams(null, isSuperUser),
        tag: selectTagContent.key
      };

      const response =
        user && user.company
          ? await makeApiCall(api.getContents, user.company.id, fetchParams)
          : await makeApiCall(api.getContentsByUsers, user.id, fetchParams);

      const folderContents =
        response.data && response.data.data ? response.data.data : [];

      if (!folderContents.length) {
        message.info(t('No content in folder'));
        return;
      }

      addAllContentsFromFolder(folderContents);
    } catch (error) {
      console.error('triggerAddAllFromFolder error:', error.message);
      message.error(t('Error loading folder contents'));
    } finally {
      setLoadingAddAll(false);
    }
  };

  const hasOutputsInVersion = (version) =>
    customContents.some(cc => {
      if (!cc.content || (cc.content.content_version_name || 'V1') !== version || cc.active !== 1) {
        return false;
      }
      const remainingPaid = calculateImpressions(
        'to_consumed_not_subsidized',
        cc,
        reportSingleData,
        { id: cc.displayId }
      ) || 0;
      const remainingSubsidized = calculateImpressions(
        'to_consumed_subsidized',
        cc,
        reportSingleData,
        { id: cc.displayId }
      ) || 0;
      const consumedPaid = (cc.promised_shows || 0) - remainingPaid;
      const consumedSubsidized = (cc.promised_shows_subsidized || 0) - remainingSubsidized;
      return consumedPaid > 0 || consumedSubsidized > 0;
    });

  const willHardDeleteVersion = (version) => {
    const versionHasAnyContent = listOfContentsInCampaign.some(
      c => !c.isPlaceholder && (c.content_version_name || 'V1') === version
    );
    if (!versionHasAnyContent) return true;

    const versionWasSavedBeforeThisSession = !!(
      initialBackup &&
      initialBackup.customContents &&
      initialBackup.customContents.some(
        cc => cc.content && (cc.content.content_version_name || 'V1') === version
      )
    );
    if (!versionWasSavedBeforeThisSession) return true;

    const isApprovedPayment = currentCampaign && currentCampaign.payment_status === 'approved';
    return !hasOutputsInVersion(version) && !isApprovedPayment;
  };

  const deactivateRelationsInPlace = (relationsToDeactivate, newCustomContents) => {
    relationsToDeactivate.forEach(relation => {
      const idx = newCustomContents.indexOf(relation);
      if (idx === -1) return;
      const displayId = relation.displayId;

      const survivingActiveRelations = newCustomContents.filter(
        cc => cc.displayId === displayId && cc.active === 1 && cc !== relation
      );

      if (survivingActiveRelations.length === 0) {
        newCustomContents[idx] = { ...relation, active: 0, revision_status: 'inactive' };
        return;
      }

      const remainingPaid = Math.max(
        0,
        calculateImpressions('to_consumed_not_subsidized', relation, reportSingleData, { id: displayId })
      );
      const remainingSubsidized = Math.max(
        0,
        calculateImpressions('to_consumed_subsidized', relation, reportSingleData, { id: displayId }) || 0
      );

      newCustomContents[idx] = {
        ...relation,
        active: 0,
        promised_shows: (relation.promised_shows || 0) - remainingPaid,
        promised_shows_subsidized: (relation.promised_shows_subsidized || 0) - remainingSubsidized,
        revision_status: 'inactive'
      };

      if (remainingPaid > 0 || remainingSubsidized > 0) {
        const { updates } = redistributePendingShows({
          relations: survivingActiveRelations,
          reportSingleData,
          displayId,
          extraPaidPool: remainingPaid,
          extraSubsidizedPool: remainingSubsidized
        });
        survivingActiveRelations.forEach((rel, i) => {
          const survivingIdx = newCustomContents.indexOf(rel);
          if (survivingIdx !== -1) newCustomContents[survivingIdx] = { ...rel, ...updates[i] };
        });
      }
    });
  };

  const deleteContentScoped = (contentId, version) => {
    if (!groupedDisplays) {
      deleteContentFromCampaign(contentId, version);
      return;
    }

    const groupedDisplayIds = groupedDisplays.map(d => d.id);
    const matchesTarget = versionName => !version || (versionName || 'V1') === version;
    const activeRelationsInGroup = customContents.filter(
      cc =>
        cc.content &&
        cc.content.id === contentId &&
        matchesTarget(cc.content.content_version_name) &&
        groupedDisplayIds.includes(cc.displayId) &&
        cc.active === 1
    );

    const isApprovedCampaign = currentCampaign && currentCampaign.payment_status === 'approved';
    const wasSavedBeforeThisSession = cc =>
      !!(
        initialBackup &&
        initialBackup.customContents &&
        initialBackup.customContents.some(
          backupCc =>
            backupCc.displayId === cc.displayId &&
            backupCc.content &&
            backupCc.content.id === cc.content.id &&
            (backupCc.content.content_version_name || 'V1') === (cc.content.content_version_name || 'V1')
        )
      );

    const relationsToDeactivate = activeRelationsInGroup.filter(
      cc => isApprovedCampaign && wasSavedBeforeThisSession(cc)
    );
    const relationsToRemove = activeRelationsInGroup.filter(cc => !relationsToDeactivate.includes(cc));

    const newCustomContents = customContents.filter(cc => !relationsToRemove.includes(cc));
    if (relationsToDeactivate.length > 0) {
      deactivateRelationsInPlace(relationsToDeactivate, newCustomContents);
    }
    if (relationsToRemove.length > 0 || relationsToDeactivate.length > 0) {
      updateCustomContents(newCustomContents);
    }

    const stillReferencedElsewhere = newCustomContents.some(
      cc => cc.content && cc.content.id === contentId && matchesTarget(cc.content.content_version_name)
    );
    if (!stillReferencedElsewhere) {
      setListOfContentsInCampaign(
        listOfContentsInCampaign.filter(c => !(c.id === contentId && matchesTarget(c.content_version_name)))
      );
    }
  };

  const deleteVersion = (version) => {
    if (groupedDisplays) {
      const groupedDisplayIds = groupedDisplays.map(d => d.id);
      const newCustomContents = [...customContents];
      const activeRelationsInGroup = newCustomContents.filter(
        cc =>
          cc.content &&
          (cc.content.content_version_name || 'V1') === version &&
          groupedDisplayIds.includes(cc.displayId) &&
          cc.active === 1
      );

      if (activeRelationsInGroup.length > 0) {
        const wouldLeaveScreenEmpty = activeRelationsInGroup.some(relation => {
          const survivingActiveRelations = newCustomContents.filter(
            cc => cc.displayId === relation.displayId && cc.active === 1 && cc !== relation
          );
          return survivingActiveRelations.length === 0;
        });
        if (wouldLeaveScreenEmpty) {
          message.error(t('This version cannot be deleted: at least one screen would be left without any ad.'));
          return;
        }

        deactivateRelationsInPlace(activeRelationsInGroup, newCustomContents);
        updateCustomContents(newCustomContents);
        message.success(t('Version deactivated. Its remaining outputs were redistributed among the other active versions.'));
      }

      const orphanIdsForVersion = listOfContentsInCampaign
        .filter(
          c =>
            !c.isPlaceholder &&
            (c.content_version_name || 'V1') === version &&
            !newCustomContents.some(
              cc => cc.content && cc.content.id === c.id && (cc.content.content_version_name || 'V1') === version
            )
        )
        .map(c => c.id);

      if (orphanIdsForVersion.length > 0) {
        setListOfContentsInCampaign(
          listOfContentsInCampaign.filter(c => !orphanIdsForVersion.includes(c.id))
        );
      }
      return;
    }

    if (willHardDeleteVersion(version)) {
      listOfContentsInCampaign
        .filter(c => (c.content_version_name || 'V1') === version)
        .forEach(c => deleteContentFromCampaign(c.id, c.content_version_name || 'V1'));
      return;
    }

    let newCustomContents = [...customContents];
    const activeRelationsInVersion = newCustomContents.filter(
      cc => cc.content && (cc.content.content_version_name || 'V1') === version && cc.active === 1
    );

    activeRelationsInVersion.forEach(relation => {
      const idx = newCustomContents.indexOf(relation);
      if (idx === -1) return;
      const displayId = relation.displayId;

      const survivingActiveRelations = newCustomContents.filter(
        cc => cc.displayId === displayId && cc.active === 1 && cc !== relation
      );

      if (survivingActiveRelations.length === 0) {
        newCustomContents[idx] = { ...relation, active: 0, revision_status: 'inactive' };
        return;
      }

      const remainingPaid = Math.max(
        0,
        calculateImpressions('to_consumed_not_subsidized', relation, reportSingleData, { id: displayId })
      );
      const remainingSubsidized = Math.max(
        0,
        calculateImpressions('to_consumed_subsidized', relation, reportSingleData, { id: displayId }) || 0
      );

      newCustomContents[idx] = {
        ...relation,
        active: 0,
        promised_shows: (relation.promised_shows || 0) - remainingPaid,
        promised_shows_subsidized: (relation.promised_shows_subsidized || 0) - remainingSubsidized,
        revision_status: 'inactive'
      };

      if (remainingPaid > 0 || remainingSubsidized > 0) {
        const { updates } = redistributePendingShows({
          relations: survivingActiveRelations,
          reportSingleData,
          displayId,
          extraPaidPool: remainingPaid,
          extraSubsidizedPool: remainingSubsidized
        });
        survivingActiveRelations.forEach((rel, i) => {
          const survivingIdx = newCustomContents.indexOf(rel);
          if (survivingIdx !== -1) newCustomContents[survivingIdx] = { ...rel, ...updates[i] };
        });
      }
    });

    updateCustomContents(newCustomContents);
    message.success(t('Version deactivated. Its remaining outputs were redistributed among the other active versions.'));
  };

  return {
    contentModal,
    visibleModalContent,
    visibleModalDeleteContent,
    isModalOpen,
    isModalCreateVersionOpen,
    wouldConflictInVersion,
    visibleCanvaModal,
    alertModalVisible,
    alertModalType,
    showModalContent,
    cancelModalContent,
    showModalDeleteContent,
    updateContent,
    checkContentsAdded,
    addContents,
    addContentsFromInfoCampaign,
    dynamicAlertVisible,
    closeDynamicAlert: () => setDynamicAlertVisible(false),
    replaceVersion,
    createVersion,
    handleCancel,
    handleModalCreateVersionCancel,
    handleChangeAd,
    getChangeAdDescription,
    setVisibleCanvaModal,
    setAlertModalVisible,
    conflictModalOpen,
    conflictGroups,
    existingByResolution,
    paymentStatus: currentCampaign && currentCampaign.payment_status,
    isFolderUsed: (() => {
      const id = getFolderIdentifier(selectTagContent);
      if (!id) return false;
      const key = `${id}_${selectVersion || 'V1'}`;
      return usedFolderKeys.has(key);
    })(),
    loadingAddAll,
    addAllContentsFromFolder,
    triggerAddAllFromFolder,
    confirmAddAll,
    cancelAddAll,
    deleteVersion,
    deleteContentScoped,
    hasOutputsInVersion,
    willHardDeleteVersion
  };
};

export default useContentActions;
